import { createSeededAssistantFeatureTransport } from "./mockSeededTransport.js";
import type {
  AssistantFeatureBundle,
  AssistantFeatureBundleOptions,
  AssistantFeatureCapabilitySeed,
  AssistantFeatureRouteFamily,
} from "./types.js";

export type {
  AssistantFeatureBundle,
  AssistantFeatureBundleOptions,
  AssistantFeatureBundlePreviewProps,
  AssistantFeatureCapabilitySeed,
  AssistantFeatureFeedbackAck,
  AssistantFeatureFeedbackInput,
  AssistantFeatureGeneratedArtifactFile,
  AssistantFeatureLocalDraftActionHandler,
  AssistantFeatureLocalDraftActionResult,
  AssistantFeatureModelOption,
  AssistantFeaturePersona,
  AssistantFeatureReportHistoryEntry,
  AssistantFeatureReportTemplate,
  AssistantFeatureRouteFamily,
  AssistantFeatureRouteManifest,
  AssistantFeatureRuntimeContext,
  AssistantFeatureRuntimePolicy,
  AssistantFeatureShellProps,
  AssistantFeatureTransport,
  AssistantFeatureWorkspacePayload,
} from "./types.js";

export {
  AssistantFeatureBundlePreview,
  AssistantFeatureShell,
} from "./views/AssistantFeatureShell.js";
export {
  projectAssistantConversationRecentItems,
} from "./projectors/assistantConversationRecentItemsProjector.js";
export {
  projectAssistantFilesTrayItems,
  type AssistantFilesTrayItem,
  type AssistantFilesTrayItemSource,
} from "./projectors/assistantFilesTrayProjector.js";

const DEFAULT_ROUTE_LABELS: Record<AssistantFeatureRouteFamily, string> = {
  "assistant-home": "Assistant Home",
  admissions: "Admissions",
  "financial-aid": "Financial Aid",
  settings: "Settings",
  "students-families": "Students & Families",
  attendance: "Attendance",
  academics: "Academics",
  substitutes: "Substitutes",
  faculty: "Faculty",
  finance: "Finance",
  payroll: "Payroll",
  camps: "Camps",
  donations: "Donations",
  "tip-a-teacher": "Tip-a-Teacher",
  tuition: "Tuition",
  "run-reports": "Run Reports",
  "parent-portal": "Parent Portal",
  general: "General",
};

const STAFF_PERSONAS = ["staff", "administrator", "super-administrator", "ops"];

const createAdvancedVisualizationSeed = ({
  routeFamily,
  label,
  module,
  prefix,
  source,
}: {
  routeFamily: AssistantFeatureRouteFamily;
  label: string;
  module?: string;
  prefix: string;
  source: string;
}): AssistantFeatureCapabilitySeed => ({
  id: `${routeFamily}-advanced-visualization`,
  title: `Map ${label} workflow risks`,
  prompt: `Build a route-scoped 3D workflow view for ${label.toLowerCase()} that groups the active records by status, owner, follow-up need, and recommended next step.`,
  rationale: `${label} staff need a spatial artifact view that stays scoped to the active route family.`,
  module: module ?? routeFamily,
  scenarioKey: "advanced-visualization",
  method: `${routeFamily}.route.visualization3d`,
  routePrefixes: [prefix],
  personas: STAFF_PERSONAS,
  likelySources: [source],
  dataStateSignals: ["artifact", "threejs", "visualization3d", "route-context"],
  type: "action",
  taskClass: "artifact_generate",
  workflowKey: "artifact.visualization3d.generate",
  artifactKind: "visualization3d",
  seededPresentation: {
    summary: `I can stage an interactive Three.js ${label.toLowerCase()} visualization with status, owner, urgency, and next-action clusters.`,
    artifactKind: "visualization3d",
    artifactTitle: `${label} advanced route visualization`,
  },
});

const DEFAULT_CAPABILITY_SEEDS: Record<
  AssistantFeatureRouteFamily,
  AssistantFeatureCapabilitySeed[]
> = {
  "assistant-home": [],
  admissions: [
    {
      id: "admissions-dashboard-summary",
      title: "Estimate enrollment yield",
      prompt:
        "Estimate enrollment yield for the current admissions dashboard and flag the riskiest follow-up gaps.",
      rationale:
        "Admissions staff need a fast summary that keeps active route context intact.",
      module: "admissions",
      scenarioKey: "dashboard-summary",
      method: "admissions.dashboard.summary",
      routePrefixes: ["/school/admissions"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "listTimelineEvents"],
      dataStateSignals: ["pipeline", "followup"],
      type: "insight",
      seededPresentation: {
        summary:
          "The admissions dashboard is stable, but three families still need follow-up before the next review block.",
        metrics: [
          {
            label: "Open applicants",
            value: "24",
            detail: "current dashboard scope",
            tone: "info",
          },
          {
            label: "At risk",
            value: "3",
            detail: "missing follow-up",
            tone: "warning",
          },
        ],
        checklist: [
          {
            label: "Call Silver family",
            status: "ready",
            detail: "missing interview confirmation",
          },
          {
            label: "Review Cohen timeline",
            status: "watch",
            detail: "parent questions still open",
          },
        ],
        body: "This deterministic seeded summary keeps the route-aware admissions context intact while the shared assistant bundle is proven in the demo lane.",
        links: [
          {
            label: "Open admissions dashboard",
            href: "/school/admissions/dashboard",
            meta: "current route",
          },
        ],
      },
    },
    {
      id: "admissions-threejs-advanced-visualization",
      title: "Advanced Three.js visualization",
      prompt:
        "generate an advanced visualization of all admissions data using threejs",
      rationale:
        "Admissions staff need a spatial artifact view that remains scoped to the live dashboard data window.",
      module: "admissions",
      scenarioKey: "threejs-advanced-visualization",
      method: "admissions.dashboard.visualization3d.full",
      routePrefixes: ["/school/admissions"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "listTimelineEvents"],
      dataStateSignals: ["artifact", "threejs", "visualization3d"],
      type: "action",
      taskClass: "artifact_generate",
      workflowKey: "artifact.visualization3d.generate",
      artifactKind: "visualization3d",
      seededPresentation: {
        summary:
          "I can package an interactive Three.js admissions space and keep the inline preview hydrated from the saved artifact.",
        metrics: [
          {
            label: "3D artifact",
            value: "previewable",
            detail: "Three.js space workflow",
            tone: "success",
          },
        ],
        artifactKind: "visualization3d",
        artifactTitle: "Admissions 3D visualization",
        links: [
          {
            label: "Stay on admissions dashboard",
            href: "/school/admissions/dashboard",
            meta: "no route change required",
          },
        ],
      },
    },
    {
      id: "admissions-status-chart",
      title: "Create a bar chart by status",
      prompt: "Create a bar chart for the admissions dashboard by status.",
      rationale:
        "Dashboard chart generation should flow through the background task lane.",
      module: "admissions",
      scenarioKey: "status-chart",
      method: "admissions.dashboard.chart.status",
      routePrefixes: ["/school/admissions"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications"],
      dataStateSignals: ["artifact", "chart"],
      type: "action",
      taskClass: "artifact_generate",
      workflowKey: "artifact.chart.generate",
      artifactKind: "chart",
      seededPresentation: {
        summary:
          "I can package a deterministic chart artifact in the background and keep the task lane updated while you keep working.",
        metrics: [
          {
            label: "Artifact lane",
            value: "ready",
            detail: "background chart workflow",
            tone: "success",
          },
        ],
        artifactKind: "chart",
        artifactTitle: "Admissions status trend chart",
        links: [
          {
            label: "Stay on admissions dashboard",
            href: "/school/admissions/dashboard",
            meta: "no route change required",
          },
        ],
      },
    },
    {
      id: "admissions-export-spreadsheet",
      title: "Identify pipeline bottlenecks",
      prompt:
        "Identify admissions pipeline bottlenecks from the current dashboard and recommend the next staff actions.",
      rationale:
        "Pipeline bottleneck review should flow through the background task lane.",
      module: "admissions",
      scenarioKey: "spreadsheet-export",
      method: "admissions.dashboard.export.spreadsheet",
      routePrefixes: ["/school/admissions"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "listTimelineEvents"],
      dataStateSignals: ["artifact", "spreadsheet"],
      type: "action",
      taskClass: "artifact_generate",
      workflowKey: "artifact.spreadsheet.generate",
      artifactKind: "spreadsheet",
      seededPresentation: {
        summary:
          "I can stage a deterministic spreadsheet artifact and open the workbook preview once the task completes.",
        metrics: [
          {
            label: "Workbook",
            value: "previewable",
            detail: "seeded export workspace",
            tone: "success",
          },
        ],
        artifactKind: "spreadsheet",
        artifactTitle: "Admissions export workbook",
      },
    },
  ],
  "financial-aid": [
    {
      id: "financial-aid-review-priority",
      title: "Prioritize pending reviews",
      prompt:
        "Prioritize the pending financial-aid reviews by readiness and exposure.",
      rationale:
        "The assistant should summarize the queue without leaving the route.",
      module: "financial-aid",
      scenarioKey: "priority-review",
      method: "financial-aid.queue.priority",
      routePrefixes: ["/school/financialaid"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "listTimelineEvents"],
      dataStateSignals: ["queue", "review"],
      type: "insight",
      seededPresentation: {
        summary:
          "The next financial-aid review block should start with two ready applications and one application that still needs parent contact.",
        metrics: [
          {
            label: "Ready first",
            value: "2",
            detail: "full packet available",
            tone: "success",
          },
        ],
        checklist: [
          {
            label: "Call Adler family",
            status: "blocked",
            detail: "missing household clarification",
          },
        ],
      },
    },
    createAdvancedVisualizationSeed({
      routeFamily: "financial-aid",
      label: "Financial Aid",
      module: "financial-aid",
      prefix: "/school/financialaid",
      source: "financialAid.review",
    }),
  ],
  settings: [
    {
      id: "settings-policy-readiness",
      title: "Review settings policy readiness",
      prompt:
        "Review settings policy readiness and flag the riskiest downstream issue.",
      rationale:
        "Settings changes affect multiple route families and need route-aware explanation.",
      module: "settings",
      scenarioKey: "settings-readiness",
      method: "settings.review.policy",
      routePrefixes: ["/school/settings"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "previewAssistantAction"],
      dataStateSignals: ["policy", "settings"],
      type: "insight",
      seededPresentation: {
        summary:
          "Settings are mostly ready, but the next risky drift is admissions policy visibility across parent-facing surfaces.",
        metrics: [
          { label: "Risk", value: "admissions visibility", tone: "warning" },
        ],
        links: [
          {
            label: "Open school settings",
            href: "/school/settings/general",
            meta: "current route family",
          },
        ],
      },
    },
    createAdvancedVisualizationSeed({
      routeFamily: "settings",
      label: "Settings",
      module: "settings",
      prefix: "/school/settings",
      source: "settings.configuration",
    }),
  ],
  "students-families": [
    {
      id: "students-family-summary",
      title: "Summarize family record readiness",
      prompt:
        "Summarize the current family record and highlight the next staff follow-up.",
      rationale:
        "Students and families workflows need route-aware summaries with no transport leakage.",
      module: "students-families",
      scenarioKey: "family-summary",
      method: "students-families.summary",
      routePrefixes: ["/school/students"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications", "listTimelineEvents"],
      dataStateSignals: ["family", "records"],
      type: "insight",
      seededPresentation: {
        summary:
          "Family readiness is stable, but one guardian communication still needs acknowledgement before the record is fully clean.",
        checklist: [
          { label: "Confirm guardian acknowledgement", status: "watch" },
        ],
      },
    },
    createAdvancedVisualizationSeed({
      routeFamily: "students-families",
      label: "Students and Families",
      module: "students-families",
      prefix: "/school/students",
      source: "studentsFamilies.records",
    }),
  ],
  tuition: [
    {
      id: "tuition-account-risk",
      title: "Summarize tuition account risk",
      prompt:
        "Summarize tuition account risk from the current tuition dashboard.",
      rationale:
        "Tuition staff need a route-grounded summary of balances, payment status, aging, and billing follow-up.",
      module: "tuition",
      scenarioKey: "account-risk",
      method: "tuition.accounts.risk",
      routePrefixes: [
        "/school/tuition",
        "/administrator/tuition",
        "/parent/portal/tuition",
      ],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["getSchoolTuitionDashboardTree", "tuition.records"],
      dataStateSignals: ["tuition", "payment-risk", "aging"],
      type: "insight",
      seededPresentation: {
        summary:
          "Tuition account risk is concentrated in families with open balances, older aging buckets, and pending billing follow-up.",
        metrics: [
          {
            label: "Risk signal",
            value: "payment follow-up",
            detail: "tuition dashboard context",
            tone: "warning",
          },
        ],
        checklist: [
          {
            label: "Review open balances",
            status: "ready",
            detail: "group by aging bucket and payment status",
          },
          {
            label: "Assign collection follow-up",
            status: "watch",
            detail: "prioritize families with current due balances",
          },
        ],
      },
    },
    {
      id: "tuition-aging-chart",
      title: "Chart unpaid tuition balances",
      prompt:
        "Chart unpaid tuition balances by aging bucket and payment status.",
      rationale:
        "Tuition artifact generation should preserve tuition datasource grounding.",
      module: "tuition",
      scenarioKey: "aging-chart",
      method: "tuition.accounts.chart.aging",
      routePrefixes: ["/school/tuition", "/administrator/tuition"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["getSchoolTuitionDashboardTree", "tuition.records"],
      dataStateSignals: ["artifact", "chart", "payment-risk"],
      type: "action",
      taskClass: "artifact_generate",
      workflowKey: "artifact.chart.generate",
      artifactKind: "chart",
      seededPresentation: {
        summary:
          "I can package tuition aging and payment-status risk into a chart artifact.",
        artifactKind: "chart",
        artifactTitle: "Tuition aging risk chart",
      },
    },
    createAdvancedVisualizationSeed({
      routeFamily: "tuition",
      label: "Tuition",
      module: "tuition",
      prefix: "/school/tuition",
      source: "tuition.records",
    }),
  ],
  "run-reports": [],
  attendance: [
    createAdvancedVisualizationSeed({
      routeFamily: "attendance",
      label: "Attendance",
      module: "general",
      prefix: "/school/attendance",
      source: "attendance.records",
    }),
  ],
  academics: [
    createAdvancedVisualizationSeed({
      routeFamily: "academics",
      label: "Academics",
      module: "general",
      prefix: "/school/academics",
      source: "academics.records",
    }),
  ],
  substitutes: [
    createAdvancedVisualizationSeed({
      routeFamily: "substitutes",
      label: "Substitutes",
      module: "general",
      prefix: "/school/substitutes",
      source: "substitutes.records",
    }),
  ],
  faculty: [
    createAdvancedVisualizationSeed({
      routeFamily: "faculty",
      label: "Faculty",
      module: "faculty",
      prefix: "/school/faculty",
      source: "faculty.records",
    }),
  ],
  finance: [
    createAdvancedVisualizationSeed({
      routeFamily: "finance",
      label: "Finance",
      module: "general",
      prefix: "/school/finance",
      source: "finance.records",
    }),
  ],
  payroll: [
    createAdvancedVisualizationSeed({
      routeFamily: "payroll",
      label: "Payroll",
      module: "general",
      prefix: "/school/payroll",
      source: "payroll.records",
    }),
  ],
  camps: [
    createAdvancedVisualizationSeed({
      routeFamily: "camps",
      label: "Camps",
      module: "general",
      prefix: "/school/camps",
      source: "camps.records",
    }),
  ],
  donations: [
    createAdvancedVisualizationSeed({
      routeFamily: "donations",
      label: "Donations",
      module: "general",
      prefix: "/school/donations",
      source: "donations.records",
    }),
  ],
  "tip-a-teacher": [
    createAdvancedVisualizationSeed({
      routeFamily: "tip-a-teacher",
      label: "Tip-a-Teacher",
      module: "general",
      prefix: "/school/tip-a-teacher",
      source: "tip.records",
    }),
  ],
  "parent-portal": [],
  general: [
    {
      id: "general-route-summary",
      title: "Summarize the current route context",
      prompt:
        "Summarize the current route context and next suggested assistant action.",
      rationale:
        "Fallback route summary when no route-specific capability is stronger.",
      module: "general",
      scenarioKey: "general-summary",
      method: "general.route.summary",
      routePrefixes: ["/school"],
      personas: ["staff", "administrator", "super-administrator", "ops"],
      likelySources: ["listApplications"],
      dataStateSignals: ["route"],
      type: "insight",
      seededPresentation: {
        summary:
          "The shared assistant bundle is mounted and route-aware. Use seeded prompts to prove shell, task lane, and artifact behavior before real prompt acceptance.",
      },
    },
  ],
};

const DEFAULT_MODEL_OPTIONS = [
  { label: "Seeded Fast", value: "seeded-fast" },
  { label: "Seeded Reasoner", value: "seeded-reasoner" },
];

const createDefaultRouteManifest = (routeBase: string) => ({
  routeBase,
  routeLabels: DEFAULT_ROUTE_LABELS,
  resolveRouteFamily: (pathname: string): AssistantFeatureRouteFamily => {
    const normalized = String(pathname || "/").toLowerCase();
    if (normalized.startsWith("/school/admissions")) return "admissions";
    if (
      normalized.startsWith("/school/financialaid") ||
      normalized.startsWith("/school/financial-aid")
    )
      return "financial-aid";
    if (normalized.startsWith("/school/settings")) return "settings";
    if (normalized.startsWith("/school/students")) return "students-families";
    if (normalized.startsWith("/school/attendance")) return "attendance";
    if (normalized.startsWith("/school/academics")) return "academics";
    if (normalized.startsWith("/school/substitutes")) return "substitutes";
    if (normalized.startsWith("/school/faculty")) return "faculty";
    if (normalized.startsWith("/school/finance")) return "finance";
    if (normalized.startsWith("/school/payroll")) return "payroll";
    if (normalized.startsWith("/school/camps")) return "camps";
    if (normalized.startsWith("/school/donations")) return "donations";
    if (normalized.startsWith("/school/tip-a-teacher")) return "tip-a-teacher";
    if (normalized.startsWith("/school/run-reports")) return "run-reports";
    if (
      normalized.startsWith("/school/tuition") ||
      normalized.startsWith("/administrator/tuition") ||
      normalized.startsWith("/parent/portal/tuition")
    )
      return "tuition";
    if (normalized.startsWith("/parent/portal")) return "parent-portal";
    if (normalized === "/school" || normalized.startsWith("/school/assistant"))
      return "assistant-home";
    return "general";
  },
});

export const createAssistantFeatureBundle = ({
  transport,
  routeBase,
  routeManifest,
  modelOptions = DEFAULT_MODEL_OPTIONS,
  capabilitySeeds = DEFAULT_CAPABILITY_SEEDS,
  storybookInitialPathname = "/school/admissions/dashboard",
}: AssistantFeatureBundleOptions): AssistantFeatureBundle => ({
  id: "assistant",
  transport,
  routeManifest,
  modelOptions,
  capabilitySeeds,
  storybookFixtures: {
    routeBase,
    initialPathname: storybookInitialPathname,
  },
});

export const createMockAssistantFeatureBundle = (
  input: {
    routeBase?: string;
    modelOptions?: AssistantFeatureBundle["modelOptions"];
    capabilitySeeds?: AssistantFeatureBundle["capabilitySeeds"];
  } = {},
): AssistantFeatureBundle => {
  const routeBase = input.routeBase ?? "/school";
  const routeManifest = createDefaultRouteManifest(routeBase);
  const capabilitySeeds = input.capabilitySeeds ?? DEFAULT_CAPABILITY_SEEDS;

  return createAssistantFeatureBundle({
    transport: createSeededAssistantFeatureTransport({
      routeManifest,
      capabilitySeeds,
    }),
    routeBase,
    routeManifest,
    capabilitySeeds,
    modelOptions: input.modelOptions ?? DEFAULT_MODEL_OPTIONS,
    storybookInitialPathname: "/school/admissions/dashboard",
  });
};
